/**
 * Small focused dialog for editing a REQ's header-level 受注分類.
 *
 * Two modes:
 *   1. Single-REQ  → pass `req={reqObject}`. Dialog shows the target REQ's
 *      no + product name, PATCHes the single record.
 *   2. Bulk        → pass `bulk={{ ids:[str], count:N, hint?:str, orderType? }}`.
 *      Dialog shows「N件の依頼を一括更新」+ optional preview hint (like
 *      「WO-0091, WO-0108 など」), POSTs the bulk endpoint.
 *
 * Shared by /shizai/requests (list) and /shizai/kanban (board) so buyers
 * can assign 受注分類 without leaving whichever surface they were on. WO /
 * 補充提案 / parts_request-generated REQs land with a null category by
 * design; this dialog is the single place to fix that.
 */
import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { shizaiApi } from './shizaiApi';

const NONE_VAL = '__none__';

export default function CategoryEditDialog({ req, bulk, categories, onClose, onSaved }) {
  const isBulk = Boolean(bulk);
  const [pick, setPick] = useState(!isBulk ? (req?.order_category_id || NONE_VAL) : NONE_VAL);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const value = pick === NONE_VAL ? null : pick;
      if (isBulk) {
        const res = await shizaiApi.bulkPatchRequestCategory(bulk.ids, value);
        toast.success(`${res.modified}件の受注分類を更新しました`);
      } else {
        await shizaiApi.patchRequestCategory(req.id, value);
        toast.success('受注分類を更新しました');
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || '受注分類の更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const activeType = isBulk ? (bulk.orderType || 'purchase') : (req.order_type || 'purchase');
  // Show every active category. Prefer type-matching ones at the top so the
  // buyer's first click still lands on the right one, but never hide the
  // rest — hiding leads to "why is my dropdown empty" confusion when the
  // REQ's order_type doesn't line up with how categories were registered.
  const filtered = (categories || [])
    .filter((c) => c.active !== false)
    .slice()
    .sort((a, b) => {
      const aMatch = !a.order_type || a.order_type === activeType || a.order_type === 'both';
      const bMatch = !b.order_type || b.order_type === activeType || b.order_type === 'both';
      if (aMatch !== bMatch) return aMatch ? -1 : 1;
      // fall back to backend's sort_order-then-name order
      return 0;
    });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="request-cat-edit-dialog">
        <DialogHeader>
          <DialogTitle>{isBulk ? '受注分類を一括設定' : '受注分類を設定'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="rounded-lg border border-zinc-200 bg-zinc-50/70 px-3 py-2">
            <div className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide">対象</div>
            {isBulk ? (
              <>
                <div className="mt-0.5 text-sm font-semibold text-zinc-800">{bulk.count}件の依頼</div>
                {bulk.hint && (
                  <div className="text-xs text-zinc-500 truncate" title={bulk.hint}>{bulk.hint}</div>
                )}
              </>
            ) : (
              <>
                <div className="mt-0.5 text-sm font-semibold text-zinc-800">{req.request_no}</div>
                {req.product_name && <div className="text-xs text-zinc-500"><span data-no-translate>{req.product_name}</span></div>}
              </>
            )}
          </div>
          <div>
            <Label className="text-xs font-semibold text-zinc-600">受注分類</Label>
            <Select value={pick} onValueChange={setPick}>
              <SelectTrigger data-testid="request-cat-edit-select" className="h-10 mt-1">
                <SelectValue placeholder="分類を選択" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VAL}>なし</SelectItem>
                {filtered.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.code ? `${c.code} · ` : ''}{c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-zinc-400 mt-1">
              {isBulk
                ? '選択した依頼すべてに同じ分類が設定されます。既に別の分類が設定されていた場合は上書きされます。'
                : '変更後、この依頼から生成される発注は自動的にこの分類を継承します。'}
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>キャンセル</Button>
          <Button
            onClick={save}
            disabled={saving}
            className="bg-teal-700 hover:bg-teal-800"
            data-testid="request-cat-edit-save"
          >
            {saving ? '保存中...' : (isBulk ? `${bulk.count}件を更新` : '保存')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
